/**
 * WEB-901 — снятие дубля «Terms/Privacy» в блоке «Чекбокс условий».
 *
 * Рендер `terms_checkbox` дописывает к тексту названия документов ссылками,
 * если автор их сам не упомянул. Автор, написавший руками полную фразу
 * «…I agree to the Terms of Use and Privacy Policy», получал на боевой
 * странице дубль: «…Terms of Use and Privacy PolicyTerms and Privacy Policy».
 * Для AI-пути это уже лечит пост-ген-гард
 * `backend/src/ai-funnels/strip-terms-duplicate.util.ts` (WEB-896), но
 * РУЧНОЙ ввод в редакторе через него не проходит.
 *
 * Место правки — РЕНДЕР, а не сохранение блока (решение по варианту из карты):
 *  - лечит уже сохранённый контент (воронки, где дубль давно лежит в БД),
 *    а не только новые правки;
 *  - авторский текст в редакторе остаётся нетронутым — мы не переписываем
 *    молча то, что человек набрал (и не ловим гонку с автосейвом);
 *  - одна точка на все поверхности (публичная страница, превью `?preview=1`,
 *    канвас конструктора) — расходиться им нечем.
 *
 * ВОЗВРАТ ИЗ QA 10.08 (прогон, зонт WEB-1121, дорожка L3) — 5-й заход, и это
 * не докрутка предыдущей ошибки, а смена подхода целиком.
 *
 * Все предыдущие версии (1-4) работали по схеме «извлечь и переставить»:
 * находили названия документов в авторском тексте, ВЫРЕЗАЛИ их оттуда и
 * рендерили заново отдельным блоком в конце (там, где рендер и так дописывает
 * ссылки). Пока названия стояли у автора в конце фразы, это выглядело рабочим
 * — потому и проходило все предыдущие раунды QA. Как только названия стоят
 * В СЕРЕДИНЕ («…our Terms of Service and Privacy Policy, and confirm…»),
 * эффект виден: связный кусок вырезается из середины и переставляется в
 * конец, оставляя рваный обрубок с висящей пунктуацией на месте шва —
 * «our, and confirm you are over 18 Terms of Service and Privacy Policy».
 *
 * Разница между «извлечь и переставить» и «просто не трогать» — и есть суть
 * фикса. Новый контракт: авторский текст НИКОГДА не режется и не
 * переставляется. Если названия документов уже упомянуты — они превращаются
 * в ссылки ПРЯМО НА МЕСТЕ упоминания (текст автора становится кликабельным
 * ровно там, где он его написал); ничего не удаляется и ничего не
 * дописывается по этим двум документам. Если упоминания нет — поведение
 * прежнее: рендер дописывает ссылку после текста (см. PublicBlockRenderer).
 *
 * Операция идемпотентна: спан, уже несущий нужный href на всё своё
 * содержимое, повторным проходом не трогается (некому - текст не меняется).
 *
 * Словарь названий — тот же, что был у гарда до этого захода (порт
 * бэкендового гарда WEB-896, плюс русские падежные окончания: модель пишет
 * именительный/винительный, а человек руками — творительный).
 */
import type { RichField, RichSpan } from '@/types/rich-text';
import { richFieldToPlainText } from '@/types/rich-text';

/**
 * WEB-2384 п.6: образцы распознавания названий документов больше не набраны
 * тут руками на двух языках — они строятся из реестра `legalDocNames.ts` под
 * язык воронки, тем же кодом, что и на сервере. До правки правило знало только
 * английские и русские названия, и на немецком, испанском, японском,
 * украинском пейволле упоминание документа автором не становилось ссылкой, а
 * рендер дописывал названия ещё раз — дубль в строке согласия, которую читает
 * ревью магазина приложений.
 *
 * Граница WEB-901 не сдвинута: трогаем только ОДНОЗНАЧНУЮ фразу — название
 * документа целиком. Голое слово («terms», «условия», «Bedingungen») —
 * обиходное слово обычной речи, само по себе не повод; годится только внутри
 * цепочки, где есть хотя бы одно однозначное название.
 */
// ВРЕМЕННО (23.09.2026, вопрос владельцу №5 о весе публичного входа): здесь
// импортируется ПУБЛИЧНАЯ часть реестра — английский и русский. Полный набор
// на 29 языков пробивал храповик веса первого экрана на 2,3 КБ, а поднимать
// храповик без владельца нельзя. Кабинет, редактор и предпросмотр добирают
// остальные языки импортом `./legalDocNames` со своего входа, поэтому там
// правило работает на всех языках; на публике — только на английском и
// русском, как и было до WEB-2384. ВЕРНУТЬ ПОЛНЫЙ НАБОР В ПУБЛИКУ = заменить
// строку ниже на `from './legalDocNames'` (и поднять храповик веса).
import { legalDocPatterns } from './legalDocNames.public';

interface LinkSegment {
  start: number;
  end: number;
  kind: 'terms' | 'privacy';
}

/**
 * Находит куски исходного текста, которые нужно превратить в ссылки — БЕЗ
 * удаления или перестановки чего бы то ни было. `text` — плоский текст
 * (позиции совпадают с суммарной длиной спанов RichField).
 */
function findDocLinkSegments(text: string, language?: string): LinkSegment[] {
  const {
    docRun: DOC_RUN,
    hasAnchor: HAS_ANCHOR,
    taggedDocName: TAGGED_DOC_NAME,
  } = legalDocPatterns(language);
  const segments: LinkSegment[] = [];

  DOC_RUN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = DOC_RUN.exec(text)) !== null) {
    const start = match.index + match[1].length;
    const end = start + match[2].length;
    if (end === start) {
      DOC_RUN.lastIndex += 1;
      continue;
    }
    // Сомневаемся — не трогаем: без однозначного названия внутри куска это
    // просто обиходное слово, автора не касаемся.
    if (!HAS_ANCHOR.test(match[2])) continue;

    TAGGED_DOC_NAME.lastIndex = 0;
    let sub: RegExpExecArray | null;
    while ((sub = TAGGED_DOC_NAME.exec(match[2])) !== null) {
      if (sub[0].length === 0) {
        TAGGED_DOC_NAME.lastIndex += 1;
        continue;
      }
      segments.push({
        start: start + sub.index,
        end: start + sub.index + sub[0].length,
        kind: sub[1] !== undefined ? 'terms' : 'privacy',
      });
    }
  }

  return segments;
}

/** RichField → плоский список атомарных спанов (строка целиком — один спан). */
function toAtomicSpans(value: RichField): RichSpan[] {
  if (typeof value === 'string') return value === '' ? [] : [{ text: value }];
  return value;
}

/**
 * Накладывает найденные сегменты как href на спаны, разбивая исходные спаны
 * по границам сегментов. Текст и порядок символов не меняются — только
 * появляется `href` на кусках, совпавших с названием документа.
 */
function linkifySpans(
  spans: RichSpan[],
  segments: LinkSegment[],
  urls: { termsUrl?: string; privacyUrl?: string },
): RichSpan[] {
  if (segments.length === 0) return spans;

  const out: RichSpan[] = [];
  let offset = 0;

  for (const span of spans) {
    const spanStart = offset;
    const spanEnd = offset + span.text.length;
    offset = spanEnd;

    const overlapping = segments
      .filter((s) => s.start < spanEnd && s.end > spanStart)
      .sort((a, b) => a.start - b.start);

    if (overlapping.length === 0) {
      out.push(span);
      continue;
    }

    let cursor = spanStart;
    for (const seg of overlapping) {
      const segStart = Math.max(seg.start, spanStart);
      const segEnd = Math.min(seg.end, spanEnd);

      if (cursor < segStart) {
        const text = span.text.slice(cursor - spanStart, segStart - spanStart);
        if (text !== '') out.push({ ...span, text });
      }

      const href = seg.kind === 'terms' ? urls.termsUrl : urls.privacyUrl;
      const linkedText = span.text.slice(segStart - spanStart, segEnd - spanStart);
      if (linkedText !== '') {
        const linkedSpan: RichSpan = { ...span, text: linkedText };
        if (href) linkedSpan.href = href;
        out.push(linkedSpan);
      }

      cursor = segEnd;
    }

    if (cursor < spanEnd) {
      const text = span.text.slice(cursor - spanStart, spanEnd - spanStart);
      if (text !== '') out.push({ ...span, text });
    }
  }

  return out;
}

export interface LinkifyLegalMentionsResult {
  /** Авторский текст, дословно — упоминания названий стали ссылками. */
  content: RichField | undefined | null;
  /** Упомянул ли автор условия использования (в любой форме). */
  termsMentioned: boolean;
  /** Упомянул ли автор политику конфиденциальности (в любой форме). */
  privacyMentioned: boolean;
}

/**
 * Превращает упоминания «Terms of Use»/«Privacy Policy» (и их русские формы)
 * внутри авторского текста в рабочие ссылки — НА МЕСТЕ упоминания, без
 * удаления и перестановки текста. Пустое значение возвращается как есть.
 *
 * `termsMentioned`/`privacyMentioned` говорят вызывающей стороне
 * (PublicBlockRenderer), нужно ли ещё дописывать ссылку на этот документ
 * отдельным блоком после текста — если упоминание уже нашлось и стало
 * ссылкой здесь, дописывать его снова означало бы дубль.
 */
export function linkifyLegalMentions(
  value: RichField | undefined | null,
  urls: { termsUrl?: string; privacyUrl?: string } = {},
  language?: string | null,
): LinkifyLegalMentionsResult {
  if (value == null || value === '') {
    return { content: value, termsMentioned: false, privacyMentioned: false };
  }

  const plain = richFieldToPlainText(value);
  const segments = findDocLinkSegments(plain, language ?? undefined);
  if (segments.length === 0) {
    return { content: value, termsMentioned: false, privacyMentioned: false };
  }

  const spans = toAtomicSpans(value);
  const content = linkifySpans(spans, segments, urls);

  return {
    content,
    termsMentioned: segments.some((s) => s.kind === 'terms'),
    privacyMentioned: segments.some((s) => s.kind === 'privacy'),
  };
}
